import React, { useEffect, useMemo, useRef, useState } from 'react';

const normalizeIds = (value) =>
  (Array.isArray(value) ? value : [value])
    .map((id) => Number(id))
    .filter((id, index, arr) => Number.isFinite(id) && id > 0 && arr.indexOf(id) === index);

const sameIds = (left, right) => {
  const l = normalizeIds(left).sort((a, b) => a - b);
  const r = normalizeIds(right).sort((a, b) => a - b);
  if (l.length !== r.length) {
    return false;
  }

  for (let i = 0; i < l.length; i += 1) {
    if (l[i] !== r[i]) {
      return false;
    }
  }

  return true;
};

const normalizeOptions = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      id: Number(row?.id),
      name: String(row?.name || ''),
    }))
    .filter((row) => row.id > 0 && row.name);

const normalizeFacilityOptions = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      id: Number(row?.id),
      name: String(row?.name || ''),
      license: String(row?.license || ''),
      marketId: Number(row?.marketId || 0),
    }))
    .filter((row) => row.id > 0 && row.name);

const toAlpha = (left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });

const sortSelectedFirst = (options, selectedIds, allSelected) => {
  const selected = [];
  const unselected = [];
  const selectedSet = new Set(selectedIds);

  options
    .slice()
    .sort(toAlpha)
    .forEach((option) => {
      if (allSelected || selectedSet.has(option.id)) {
        selected.push(option);
      } else {
        unselected.push(option);
      }
    });

  return [...selected, ...unselected];
};

function SelectionPills({ options, allSelected, selectedIds, onRemove, placeholder, testIdPrefix }) {
  const selectedSet = new Set(selectedIds);
  const selectedOptions = (allSelected
    ? options.slice().sort(toAlpha)
    : options.filter((option) => selectedSet.has(option.id)).sort(toAlpha));

  if (selectedOptions.length === 0) {
    return <span className="user-scope-placeholder">{placeholder}</span>;
  }

  const visible = selectedOptions.slice(0, 3);
  const remaining = selectedOptions.length - visible.length;

  return (
    <>
      {visible.map((option) => (
        <span key={option.id} className="user-scope-pill" data-testid={`${testIdPrefix}-selected-${option.id}`}>
          <span className="user-scope-pill-text">{option.name}</span>
          <button
            type="button"
            className="user-scope-pill-remove"
            onClick={(event) => {
              event.stopPropagation();
              onRemove(option.id);
            }}
            aria-label={`Remove ${option.name}`}
            data-testid={`${testIdPrefix}-remove-${option.id}`}
          >
            ×
          </button>
        </span>
      ))}
      {remaining > 0 && <span className="user-scope-pill user-scope-pill-more" data-testid={`${testIdPrefix}-selected-more`}>+{remaining}</span>}
    </>
  );
}

export default function UserAccessScopeFields({ api, organizationId, form, setValue }) {
  const [marketOptions, setMarketOptions] = useState([]);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [marketOptionsError, setMarketOptionsError] = useState(false);

  const [openField, setOpenField] = useState(null);
  const [marketSearch, setMarketSearch] = useState('');
  const [facilitySearch, setFacilitySearch] = useState('');
  const [brandSearch, setBrandSearch] = useState('');

  const marketsTriggerRef = useRef<HTMLDivElement | null>(null);
  const facilitiesTriggerRef = useRef<HTMLDivElement | null>(null);
  const brandsTriggerRef = useRef<HTMLDivElement | null>(null);
  const marketsDropdownRef = useRef<HTMLDivElement | null>(null);
  const facilitiesDropdownRef = useRef<HTMLDivElement | null>(null);
  const brandsDropdownRef = useRef<HTMLDivElement | null>(null);

  const marketsLoaded = useRef(false);
  const marketsLoadSucceeded = useRef(false);
  const facilitiesLoaded = useRef(false);
  const brandsLoaded = useRef(false);

  const allMarkets = form?.allMarkets === true;
  const allFacilities = form?.allFacilities === true;
  const allBrands = form?.allBrands === true;

  const selectedMarketIds = normalizeIds(form?.marketIds);
  const defaultMarketId = normalizeIds(form?.defaultMarketId)[0] ?? null;
  const selectedFacilityIds = normalizeIds(form?.facilityIds);
  const selectedBrandIds = normalizeIds(form?.brandIds);

  useEffect(() => {
    if (form?.allMarkets == null) setValue('allMarkets', false);
    if (form?.allFacilities == null) setValue('allFacilities', false);
    if (form?.allBrands == null) setValue('allBrands', false);
    if (!Array.isArray(form?.marketIds)) setValue('marketIds', []);
    if (form?.defaultMarketId === undefined) setValue('defaultMarketId', null);
    if (!Array.isArray(form?.facilityIds)) setValue('facilityIds', []);
    if (!Array.isArray(form?.brandIds)) setValue('brandIds', []);
  }, [form, setValue]);

  useEffect(() => {
    if (!openField) {
      return undefined;
    }

    const onPointerDown = (event) => {
      const target = event.target as Node;
      const refs = [
        marketsTriggerRef.current,
        facilitiesTriggerRef.current,
        brandsTriggerRef.current,
        marketsDropdownRef.current,
        facilitiesDropdownRef.current,
        brandsDropdownRef.current,
      ];

      if (!refs.some((ref) => ref && ref.contains(target))) {
        setOpenField(null);
      }
    };

    const onEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenField(null);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onEscape);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [openField]);

  useEffect(() => {
    if (!organizationId) {
      marketsLoaded.current = false;
      marketsLoadSucceeded.current = false;
      setMarketOptionsError(false);
      setMarketOptions([]);
      return;
    }

    marketsLoaded.current = false;
    marketsLoadSucceeded.current = false;
    setMarketOptionsError(false);
    // Clear the old organization's choices and render the saved-value loading
    // option immediately; ref changes alone do not schedule a render.
    setMarketOptions([]);
    let cancelled = false;
    api
      .getUserAccessMarkets({
        organizationId,
        facilityIds: [],
        brandIds: [],
      })
      .then((rows) => {
        if (!cancelled) {
          marketsLoaded.current = true;
          marketsLoadSucceeded.current = true;
          setMarketOptionsError(false);
          setMarketOptions(normalizeOptions(rows));
        }
      })
      .catch(() => {
        if (!cancelled) {
          marketsLoaded.current = true;
          marketsLoadSucceeded.current = false;
          setMarketOptionsError(true);
          setMarketOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, organizationId]);

  useEffect(() => {
    if (!organizationId) {
      facilitiesLoaded.current = false;
      setFacilityOptions([]);
      return;
    }

    facilitiesLoaded.current = false;
    let cancelled = false;
    api
      .getUserAccessFacilities({
        organizationId,
        marketIds: allMarkets ? [] : selectedMarketIds,
        brandIds: [],
      })
      .then((rows) => {
        if (!cancelled) {
          facilitiesLoaded.current = true;
          setFacilityOptions(normalizeFacilityOptions(rows));
        }
      })
      .catch(() => {
        if (!cancelled) {
          facilitiesLoaded.current = true;
          setFacilityOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, organizationId, allMarkets, selectedMarketIds.join(',')]);

  useEffect(() => {
    if (!organizationId) {
      brandsLoaded.current = false;
      setBrandOptions([]);
      return;
    }

    brandsLoaded.current = false;
    let cancelled = false;
    api
      .getUserAccessBrands({
        organizationId,
        marketIds: allMarkets ? [] : selectedMarketIds,
        facilityIds: allFacilities ? [] : selectedFacilityIds,
      })
      .then((rows) => {
        if (!cancelled) {
          brandsLoaded.current = true;
          setBrandOptions(normalizeOptions(rows));
        }
      })
      .catch(() => {
        if (!cancelled) {
          brandsLoaded.current = true;
          setBrandOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, organizationId, allMarkets, selectedMarketIds.join(','), allFacilities, selectedFacilityIds.join(',')]);

  useEffect(() => {
    if (!marketsLoaded.current || !marketsLoadSucceeded.current) return;

    const validIds = selectedMarketIds.filter((id) => marketOptions.some((option) => option.id === id));

    if (!sameIds(validIds, selectedMarketIds)) {
      setValue('marketIds', validIds);
      if (allMarkets) {
        setValue('allMarkets', false);
      }
      return;
    }

    if (allMarkets) {
      const optionIds = marketOptions.map((option) => option.id);
      if (!sameIds(optionIds, selectedMarketIds)) {
        setValue('marketIds', optionIds);
      }
    }
  }, [allMarkets, marketOptions, selectedMarketIds.join(','), setValue]);

  useEffect(() => {
    if (!marketsLoaded.current || !marketsLoadSucceeded.current || defaultMarketId === null) return;
    const isAccessible = marketOptions.some((option) => option.id === defaultMarketId)
      && (allMarkets || selectedMarketIds.includes(defaultMarketId));
    if (!isAccessible) setValue('defaultMarketId', null);
  }, [allMarkets, defaultMarketId, marketOptions, selectedMarketIds.join(','), setValue]);

  useEffect(() => {
    if (!facilitiesLoaded.current) return;

    const validIds = selectedFacilityIds.filter((id) => facilityOptions.some((option) => option.id === id));

    if (!sameIds(validIds, selectedFacilityIds)) {
      setValue('facilityIds', validIds);
      if (allFacilities) {
        setValue('allFacilities', false);
      }
      return;
    }

    if (allFacilities) {
      const optionIds = facilityOptions.map((option) => option.id);
      if (!sameIds(optionIds, selectedFacilityIds)) {
        setValue('facilityIds', optionIds);
      }
    }
  }, [allFacilities, facilityOptions, selectedFacilityIds.join(','), setValue]);

  useEffect(() => {
    if (!brandsLoaded.current) return;

    const validIds = selectedBrandIds.filter((id) => brandOptions.some((option) => option.id === id));

    if (!sameIds(validIds, selectedBrandIds)) {
      setValue('brandIds', validIds);
      if (allBrands) {
        setValue('allBrands', false);
      }
      return;
    }

    if (allBrands) {
      const optionIds = brandOptions.map((option) => option.id);
      if (!sameIds(optionIds, selectedBrandIds)) {
        setValue('brandIds', optionIds);
      }
    }
  }, [allBrands, brandOptions, selectedBrandIds.join(','), setValue]);

  const visibleMarketOptions = useMemo(() => {
    const term = marketSearch.trim().toLowerCase();
    const filtered = term
      ? marketOptions.filter((option) => option.name.toLowerCase().startsWith(term))
      : marketOptions;

    return sortSelectedFirst(filtered, selectedMarketIds, allMarkets);
  }, [marketOptions, marketSearch, selectedMarketIds.join(','), allMarkets]);

  const visibleFacilityOptions = useMemo(() => {
    const term = facilitySearch.trim().toLowerCase();
    const filtered = term
      ? facilityOptions.filter((option) => {
          const name = option.name.toLowerCase();
          const license = String(option.license || '').toLowerCase();
          return name.includes(term) || license.includes(term);
        })
      : facilityOptions;

    return sortSelectedFirst(filtered, selectedFacilityIds, allFacilities);
  }, [facilityOptions, facilitySearch, selectedFacilityIds.join(','), allFacilities]);

  const visibleBrandOptions = useMemo(() => {
    const term = brandSearch.trim().toLowerCase();
    const filtered = term
      ? brandOptions.filter((option) => option.name.toLowerCase().includes(term))
      : brandOptions;

    return sortSelectedFirst(filtered, selectedBrandIds, allBrands);
  }, [brandOptions, brandSearch, selectedBrandIds.join(','), allBrands]);

  const setAllForField = (field, enabled, optionIds) => {
    if (field === 'market') {
      setValue('allMarkets', enabled);
      setValue('marketIds', enabled ? optionIds : []);
      return;
    }

    if (field === 'facility') {
      setValue('allFacilities', enabled);
      setValue('facilityIds', enabled ? optionIds : []);
      return;
    }

    setValue('allBrands', enabled);
    setValue('brandIds', enabled ? optionIds : []);
  };

  const toggleOption = (field, optionId, optionIds, selectedIds, allSelected) => {
    if (allSelected) {
      const nextSelected = optionIds.filter((id) => id !== optionId);
      if (field === 'market') {
        setValue('allMarkets', false);
        setValue('marketIds', nextSelected);
      } else if (field === 'facility') {
        setValue('allFacilities', false);
        setValue('facilityIds', nextSelected);
      } else {
        setValue('allBrands', false);
        setValue('brandIds', nextSelected);
      }
      return;
    }

    const nextSelected = selectedIds.includes(optionId)
      ? selectedIds.filter((id) => id !== optionId)
      : [...selectedIds, optionId];

    if (field === 'market') {
      setValue('marketIds', nextSelected);
      return;
    }

    if (field === 'facility') {
      setValue('facilityIds', nextSelected);
      return;
    }

    setValue('brandIds', nextSelected);
  };

  return (
    <div className="user-scope-root" data-testid="user-access-scope">
      <div className="scope-divider" />

      <div className="user-scope-field" data-testid="user-access-markets-field">
        <label className="user-scope-label" htmlFor="user-access-markets-trigger">Market <span className="required-dot" aria-label="required">*</span></label>
        <div
          className={`user-scope-trigger ${openField === 'market' ? 'is-open' : ''}`}
          ref={marketsTriggerRef}
        >
          <span className="user-scope-pill-row">
            <SelectionPills
              options={marketOptions}
              allSelected={allMarkets}
              selectedIds={selectedMarketIds}
              onRemove={(optionId) => toggleOption('market', optionId, marketOptions.map((o) => o.id), selectedMarketIds, allMarkets)}
              placeholder="Search markets..."
              testIdPrefix="user-access-markets"
            />
          </span>
          <button
            id="user-access-markets-trigger"
            type="button"
            className="user-scope-caret"
            aria-label="Toggle market access options"
            aria-expanded={openField === 'market'}
            data-testid="user-access-markets-trigger"
            onClick={(event) => {
              event.stopPropagation();
              setOpenField((prev) => (prev === 'market' ? null : 'market'));
            }}
          >
            ▴
          </button>
        </div>
        {openField === 'market' && (
          <div className="user-scope-dropdown" ref={marketsDropdownRef} data-testid="user-access-markets-dropdown">
            <div className="user-scope-search-row">
              <input
                className="input user-scope-search"
                placeholder="Search markets..."
                value={marketSearch}
                onChange={(event) => setMarketSearch(event.target.value)}
                data-testid="user-access-markets-search"
              />
            </div>
            <div className="user-scope-selected-count" data-testid="user-access-markets-selected-count">
              {allMarkets ? `${marketOptions.length} selected` : `${selectedMarketIds.length} selected`}
            </div>
            <button
              type="button"
              className="user-scope-option-row"
              onClick={() => setAllForField('market', !allMarkets, marketOptions.map((option) => option.id))}
              data-testid="user-access-markets-all"
              data-selected={allMarkets}
            >
              <input type="checkbox" checked={allMarkets} readOnly />
              <span>All</span>
            </button>
            <div className="user-scope-options" data-testid="user-access-markets-options">
              {visibleMarketOptions.map((option) => {
                const checked = allMarkets || selectedMarketIds.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`user-scope-option-row ${checked ? 'is-selected' : ''}`}
                    onClick={() => toggleOption('market', option.id, marketOptions.map((o) => o.id), selectedMarketIds, allMarkets)}
                    data-testid={`user-access-markets-option-${option.id}`}
                    data-selected={checked}
                  >
                    <input type="checkbox" checked={checked} readOnly />
                    <span>{option.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="user-scope-field" data-testid="user-default-market-field">
        <label className="user-scope-label" htmlFor="user-default-market">Default market</label>
        <select
          id="user-default-market"
          className="input"
          value={defaultMarketId ?? ''}
          onChange={(event) => setValue(
            'defaultMarketId',
            event.target.value ? Number(event.target.value) : null,
          )}
          data-testid="user-default-market"
          style={{ width: '100%' }}
        >
          <option value="">No default (All markets)</option>
          {(marketOptionsError || !marketsLoaded.current) && defaultMarketId !== null && (
            <option value={defaultMarketId}>
              {marketOptionsError
                ? 'Current default market (temporarily unavailable)'
                : 'Current default market (loading)'}
            </option>
          )}
          {marketOptions
            .filter((option) => allMarkets || selectedMarketIds.includes(option.id))
            .slice()
            .sort(toAlpha)
            .map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
        </select>
        {marketOptionsError && (
          <div role="alert" style={{ color: '#a12622', fontSize: 12, marginTop: 6 }}>
            Market options could not be loaded. The current default is retained unless you select No default.
          </div>
        )}
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          Used as this user&apos;s initial Base Template market for this organization.
        </div>
      </div>

      <div className="user-scope-field" data-testid="user-access-facilities-field">
        <label className="user-scope-label" htmlFor="user-access-facilities-trigger">Facility <span className="required-dot" aria-label="required">*</span></label>
        <div
          className={`user-scope-trigger ${openField === 'facility' ? 'is-open' : ''}`}
          ref={facilitiesTriggerRef}
        >
          <span className="user-scope-pill-row">
            <SelectionPills
              options={facilityOptions}
              allSelected={allFacilities}
              selectedIds={selectedFacilityIds}
              onRemove={(optionId) => toggleOption('facility', optionId, facilityOptions.map((o) => o.id), selectedFacilityIds, allFacilities)}
              placeholder="Select facilities..."
              testIdPrefix="user-access-facilities"
            />
          </span>
          <button
            id="user-access-facilities-trigger"
            type="button"
            className="user-scope-caret"
            aria-label="Toggle facility access options"
            aria-expanded={openField === 'facility'}
            data-testid="user-access-facilities-trigger"
            onClick={(event) => {
              event.stopPropagation();
              setOpenField((prev) => (prev === 'facility' ? null : 'facility'));
            }}
          >
            ▴
          </button>
        </div>
        {openField === 'facility' && (
          <div className="user-scope-dropdown" ref={facilitiesDropdownRef} data-testid="user-access-facilities-dropdown">
            <div className="user-scope-search-row">
              <input
                className="input user-scope-search"
                placeholder="Search facilities..."
                value={facilitySearch}
                onChange={(event) => setFacilitySearch(event.target.value)}
                data-testid="user-access-facilities-search"
              />
            </div>
            <div className="user-scope-table-head">
              <span>Name</span>
              <span>License</span>
            </div>
            <button
              type="button"
              className="user-scope-option-row user-scope-option-row-facility"
              onClick={() => setAllForField('facility', !allFacilities, facilityOptions.map((option) => option.id))}
              data-testid="user-access-facilities-all"
              data-selected={allFacilities}
            >
              <span className="user-scope-option-left">
                <input type="checkbox" checked={allFacilities} readOnly />
                <span>All</span>
              </span>
              <span className="user-scope-license-col" />
            </button>
            <div className="user-scope-options" data-testid="user-access-facilities-options">
              {visibleFacilityOptions.map((option) => {
                const checked = allFacilities || selectedFacilityIds.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`user-scope-option-row user-scope-option-row-facility ${checked ? 'is-selected' : ''}`}
                    onClick={() => toggleOption('facility', option.id, facilityOptions.map((o) => o.id), selectedFacilityIds, allFacilities)}
                    data-testid={`user-access-facilities-option-${option.id}`}
                    data-selected={checked}
                  >
                    <span className="user-scope-option-left">
                      <input type="checkbox" checked={checked} readOnly />
                      <span>{option.name}</span>
                    </span>
                    <span className="user-scope-license-col">{option.license || '—'}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="user-scope-field" data-testid="user-access-brands-field">
        <label className="user-scope-label" htmlFor="user-access-brands-trigger">Brand <span className="required-dot" aria-label="required">*</span></label>
        <div
          className={`user-scope-trigger ${openField === 'brand' ? 'is-open' : ''}`}
          ref={brandsTriggerRef}
        >
          <span className="user-scope-pill-row">
            <SelectionPills
              options={brandOptions}
              allSelected={allBrands}
              selectedIds={selectedBrandIds}
              onRemove={(optionId) => toggleOption('brand', optionId, brandOptions.map((o) => o.id), selectedBrandIds, allBrands)}
              placeholder="Select brands..."
              testIdPrefix="user-access-brands"
            />
          </span>
          <button
            id="user-access-brands-trigger"
            type="button"
            className="user-scope-caret"
            aria-label="Toggle brand access options"
            aria-expanded={openField === 'brand'}
            data-testid="user-access-brands-trigger"
            onClick={(event) => {
              event.stopPropagation();
              setOpenField((prev) => (prev === 'brand' ? null : 'brand'));
            }}
          >
            ▴
          </button>
        </div>
        {openField === 'brand' && (
          <div className="user-scope-dropdown" ref={brandsDropdownRef} data-testid="user-access-brands-dropdown">
            <div className="user-scope-search-row">
              <input
                className="input user-scope-search"
                placeholder="Search brands..."
                value={brandSearch}
                onChange={(event) => setBrandSearch(event.target.value)}
                data-testid="user-access-brands-search"
              />
            </div>
            <button
              type="button"
              className="user-scope-option-row"
              onClick={() => setAllForField('brand', !allBrands, brandOptions.map((option) => option.id))}
              data-testid="user-access-brands-all"
              data-selected={allBrands}
            >
              <input type="checkbox" checked={allBrands} readOnly />
              <span>All</span>
            </button>
            <div className="user-scope-brand-grid" data-testid="user-access-brands-options">
              {visibleBrandOptions.map((option) => {
                const checked = allBrands || selectedBrandIds.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`user-scope-brand-card ${checked ? 'is-selected' : ''}`}
                    onClick={() => toggleOption('brand', option.id, brandOptions.map((o) => o.id), selectedBrandIds, allBrands)}
                    data-testid={`user-access-brands-option-${option.id}`}
                    data-selected={checked}
                  >
                    <div className="user-scope-brand-media">
                      <img src="/icons/brand-management.svg" alt="" />
                    </div>
                    <div className="user-scope-brand-name">{option.name}</div>
                    <div
                      className={`user-scope-brand-check ${checked ? 'is-selected' : ''}`}
                      data-testid={`user-access-brands-selected-indicator-${option.id}`}
                      data-selected={checked}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
